import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  styled,
  ButtonGroup,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import FolderIcon from '@mui/icons-material/Folder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CategoryIcon from '@mui/icons-material/Category';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import ImageUploadModal from './ImageUploadModal';
import ImagePreviewModal from './ImagePreviewModal';
import ContextMenu from './ContextMenu';
import FolderEditModal from './FolderEditModal';
import FolderDeleteModal from './FolderDeleteModal';
import ImageContextMenu from './ImageContextMenu';
import ImageEditModal from './ImageEditModal';
import ImageMoveModal from './ImageMoveModal';
import ImageDeleteModal from './ImageDeleteModal';
import ImageInfoModal from './ImageInfoModal';
import VideoUploadModal from './VideoUploadModal';
import BulkProductImageAssignWizard from './BulkProductImageAssignWizard';
import GuideButton from '../../common/guide/GuideButton';
import { resolveByBarcodes, BarcodeResolveResult } from './services/bulkProductImageAssign';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { styled as muiStyled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  consumePendingImageLibrary,
  IMAGE_LIBRARY_EVENT,
  ImageLibraryRequest,
} from '../../aichat/agent/uiBridge';

const folderColors = [
  '#2196F3', '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#03A9F4', '#00BCD4',
  '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800', '#FF5722',
  '#795548', '#9E9E9E', '#607D8B', '#000000'
];

// Bounce animasyonu için keyframes
const bounceKeyframes = `
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
`;

// CSS'i head'e ekle
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = bounceKeyframes;
  document.head.appendChild(style);
}

// Klasör arayüzü
interface ImageFolder {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
  imageCount?: number;
  color?: string;
  branchId?: string;
}

// Resim/Video arayüzü
interface ImageItem {
  id: string;
  name: string;
  url: string;
  thumbnailUrl: string;
  folderId: string;
  createdAt: string;
  createdBy: string;
  size?: number;
  type?: string;
  mediaType?: string;
}

const ImageLibrary: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, userDetails, branches } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  // State'ler
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState<string>('#2196F3');
  const [folderBranchId, setFolderBranchId] = useState<string>(''); // '' = şube seçilmedi
  const [branchFilter, setBranchFilter] = useState<string>('all'); // 'all' | 'none' | <branchId>
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [folders, setFolders] = useState<ImageFolder[]>([]);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState<ImageFolder | null>(null);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedView, setSelectedView] = useState<'grid' | 'list'>('grid');
  const [viewSize, setViewSize] = useState<'small' | 'medium' | 'large' | 'xlarge'>('medium');
  const [filterType, setFilterType] = useState<'all' | 'folders' | 'images'>('all');

  // ─── Akıllı arama & kategori filtresi ───
  // Klasör adı = barkod kabul edilir; barkoddan ürün/kategori çözülür.
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all'); // 'all' | 'none' | <categoryName>
  // Klasör adı (barkod, lowercase) -> ürün eşleşme bilgisi
  const [folderProductInfo, setFolderProductInfo] = useState<Map<string, BarcodeResolveResult>>(new Map());
  const [enrichingCategories, setEnrichingCategories] = useState(false);

  // ─── AI ajan köprüsü (openImageLibrary) ───────────────────────────────────
  // Klasör adı isteği, klasör listesi yüklendikten sonra çözülür; bu yüzden
  // istek önce state'e alınır, folders dolunca ikinci effect uygular.
  const [agentLibraryRequest, setAgentLibraryRequest] = useState<ImageLibraryRequest | null>(null);

  // ?bulkAssign=1 ile toplu ürün resmi sihirbazını otomatik aç
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('bulkAssign') === '1') {
      setIsBulkAssignOpen(true);
    }
  }, []);

  useEffect(() => {
    const pending = consumePendingImageLibrary();
    if (pending) setAgentLibraryRequest(pending);
    const onAgentEvent = (e: Event) => {
      consumePendingImageLibrary();
      setAgentLibraryRequest((e as CustomEvent).detail as ImageLibraryRequest);
    };
    window.addEventListener(IMAGE_LIBRARY_EVENT, onAgentEvent);
    return () => window.removeEventListener(IMAGE_LIBRARY_EVENT, onAgentEvent);
  }, []);

  useEffect(() => {
    if (!agentLibraryRequest) return;
    const req = agentLibraryRequest;

    if (req.filterType === 'all' || req.filterType === 'folders' || req.filterType === 'images') {
      setFilterType(req.filterType);
    }

    if (req.folderName === null) {
      setAgentLibraryRequest(null);
      handleBackToFolders();
      return;
    }
    if (typeof req.folderName === 'string') {
      // Klasörler henüz yüklenmediyse bekle (folders değişince tekrar denenir)
      if (folders.length === 0) return;
      const wanted = req.folderName.toLocaleLowerCase('tr-TR').trim();
      const folder =
        folders.find((f) => f.name.toLocaleLowerCase('tr-TR').trim() === wanted) ||
        folders.find((f) => f.name.toLocaleLowerCase('tr-TR').includes(wanted));
      setAgentLibraryRequest(null);
      if (folder) handleFolderClick(folder);
      return;
    }
    setAgentLibraryRequest(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentLibraryRequest, folders]);
  
  // Preview modal state'leri
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [previewImages, setPreviewImages] = useState<ImageItem[]>([]);
  
  // Context menu state'leri
  const [contextMenu, setContextMenu] = useState<{
    open: boolean;
    anchorPosition: { top: number; left: number } | null;
    folder: ImageFolder | null;
  }>({
    open: false,
    anchorPosition: null,
    folder: null
  });
  
  // Klasör düzenleme/silme modal state'leri
  const [isFolderEditModalOpen, setIsFolderEditModalOpen] = useState(false);
  const [isFolderDeleteModalOpen, setIsFolderDeleteModalOpen] = useState(false);
  const [selectedFolderForAction, setSelectedFolderForAction] = useState<ImageFolder | null>(null);
  
  // Resim context menu state'leri
  const [imageContextMenu, setImageContextMenu] = useState<{
    open: boolean;
    anchorPosition: { top: number; left: number } | null;
    image: ImageItem | null;
  }>({
    open: false,
    anchorPosition: null,
    image: null
  });
  
  // Resim düzenleme/taşıma/silme modal state'leri
  const [isImageEditModalOpen, setIsImageEditModalOpen] = useState(false);
  const [isImageMoveModalOpen, setIsImageMoveModalOpen] = useState(false);
  const [isImageDeleteModalOpen, setIsImageDeleteModalOpen] = useState(false);
  const [isImageInfoModalOpen, setIsImageInfoModalOpen] = useState(false);
  const [isVideoUploadModalOpen, setIsVideoUploadModalOpen] = useState(false);
  const [isVideoPreviewOpen, setIsVideoPreviewOpen] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string>('');
  const [previewVideoName, setPreviewVideoName] = useState<string>('');
  const [selectedImageForAction, setSelectedImageForAction] = useState<ImageItem | null>(null);

  // Drag & Drop state'leri
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  // localStorage keys
  const VIEW_SIZE_KEY = 'imageLibrary_viewSize';
  const FILTER_TYPE_KEY = 'imageLibrary_filterType';
  const BRANCH_FILTER_KEY = 'imageLibrary_branchFilter';

  // localStorage'dan tercihleri yükle
  useEffect(() => {
    try {
      const savedViewSize = localStorage.getItem(VIEW_SIZE_KEY);
      if (savedViewSize && ['small', 'medium', 'large', 'xlarge'].includes(savedViewSize)) {
        setViewSize(savedViewSize as 'small' | 'medium' | 'large' | 'xlarge');
        console.log('✅ Görünüm boyutu tercihi yüklendi:', savedViewSize);
      }

      const savedFilterType = localStorage.getItem(FILTER_TYPE_KEY);
      if (savedFilterType && ['all', 'folders', 'images'].includes(savedFilterType)) {
        setFilterType(savedFilterType as 'all' | 'folders' | 'images');
        console.log('✅ Filtre tercihi yüklendi:', savedFilterType);
      }

      const savedBranchFilter = localStorage.getItem(BRANCH_FILTER_KEY);
      if (savedBranchFilter) {
        setBranchFilter(savedBranchFilter);
        console.log('✅ Şube filtresi tercihi yüklendi:', savedBranchFilter);
      }
    } catch (error) {
      console.error('localStorage okuma hatası:', error);
    }
  }, []);

  // Görünüm boyutu değiştiğinde localStorage'a kaydet
  const handleViewSizeChange = (newSize: 'small' | 'medium' | 'large' | 'xlarge') => {
    setViewSize(newSize);
    try {
      localStorage.setItem(VIEW_SIZE_KEY, newSize);
      console.log('💾 Görünüm boyutu kaydedildi:', newSize);
    } catch (error) {
      console.error('localStorage yazma hatası:', error);
    }
  };

  // Filtre tipi değiştiğinde localStorage'a kaydet
  const handleFilterTypeChange = (newType: 'all' | 'folders' | 'images') => {
    setFilterType(newType);
    try {
      localStorage.setItem(FILTER_TYPE_KEY, newType);
      console.log('💾 Filtre tercihi kaydedildi:', newType);
    } catch (error) {
      console.error('localStorage yazma hatası:', error);
    }
  };

  // Şube filtresi değiştiğinde localStorage'a kaydet
  const handleBranchFilterChange = (newBranch: string) => {
    setBranchFilter(newBranch);
    try {
      localStorage.setItem(BRANCH_FILTER_KEY, newBranch);
      console.log('💾 Şube filtresi kaydedildi:', newBranch);
    } catch (error) {
      console.error('localStorage yazma hatası:', error);
    }
  };

  // Şube adını id'den bul
  const getBranchName = (branchId?: string): string | null => {
    if (!branchId) return null;
    const found = branches.find(b => String(b.id) === String(branchId));
    return found ? found.name : null;
  };

  // Drag & Drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    
    if (dragCounter <= 1) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragCounter(0);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      // Resim yükleme modalını aç ve dosyaları aktar
      setIsUploadModalOpen(true);
      
      // Modal açıldıktan sonra dosyaları aktarmak için bir event dispatch edelim
      setTimeout(() => {
        const event = new CustomEvent('dragDropFiles', { 
          detail: { files: imageFiles } 
        });
        window.dispatchEvent(event);
      }, 100);
    }
  };

  // Boş alan tıklama handler'ı
  const handleEmptyAreaClick = () => {
    setIsUploadModalOpen(true);
  };

  // Şirket ID'sini al ve klasörleri yükle
  useEffect(() => {
    loadCompanyAndFolders();
  }, []);

  const loadCompanyAndFolders = async () => {
    try {
      setLoading(true);
      
      // Kullanıcı oturum açmış mı kontrol et
      if (!currentUser) {
        setError(tt('Kullanıcı oturumu açık değil.', 'User session is not active.'));
        setLoading(false);
        return;
      }
      
      // Kullanıcının şirket ID'sini AuthContext'ten al
      const cid = userDetails?.companyId;
      setCompanyId(cid || null);
      
      if (!cid) {
        setError(tt('Şirket bilgisi bulunamadı.', 'Company information not found.'));
        setLoading(false);
        return;
      }
      
      // Klasörleri yükle
      await loadFolders(cid);
      
      // Ana dizin resimlerini yükle
      await loadImagesInMainDirectory();
      
    } catch (error) {
      console.error('Şirket veya klasör yüklenirken hata:', error);
      setError(tt('Veriler yüklenirken bir hata oluştu.', 'An error occurred while loading data.'));
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async (companyId: string) => {
    try {
      // ✅ SQL API'sinden klasörleri çek
      const { ImageLibraryFolderService } = await import('./services/imageLibraryService');
      const foldersList = await ImageLibraryFolderService.getAllFolders(companyId);

      // Arayüz tipine dönüştür
      const mappedFolders: ImageFolder[] = foldersList.map(folder => ({
        id: folder.id,
        name: folder.name,
        createdAt: folder.createdAt,
        createdBy: folder.createdBy || '',
        imageCount: folder.imageCount,
        color: folder.color,
        branchId: folder.branchId
      }));

      // Klasörleri oluşturulma tarihine göre sırala (en yeni en üstte)
      mappedFolders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      setFolders(mappedFolders);
      console.log('✅ Klasörler SQL\'den yüklendi:', mappedFolders.length, 'adet');

      // Klasör adlarını (barkod) ürün/kategori ile eşleştir (arama & kategori filtresi için)
      enrichFoldersWithProducts(companyId, mappedFolders);
    } catch (error) {
      console.error('❌ Klasörler yüklenirken hata:', error);
      setError(tt('Klasörler yüklenirken bir hata oluştu.', 'An error occurred while loading folders.'));
    }
  };

  // Klasör adlarını (barkod) ürüne çözerek kategori/isim bilgisini önbelleğe alır.
  // İstemci tarafı arama ve kategori filtresi bu bilgiyi kullanır.
  const enrichFoldersWithProducts = async (companyId: string, foldersList: ImageFolder[]) => {
    try {
      const barcodes = Array.from(
        new Set(
          foldersList
            .map((f) => (f.name || '').trim())
            .filter((n) => n.length > 0)
        )
      );
      if (barcodes.length === 0) {
        setFolderProductInfo(new Map());
        return;
      }

      setEnrichingCategories(true);
      const map = new Map<string, BarcodeResolveResult>();
      const CHUNK = 500;
      for (let i = 0; i < barcodes.length; i += CHUNK) {
        const chunk = barcodes.slice(i, i + CHUNK);
        try {
          const results = await resolveByBarcodes(companyId, chunk);
          results.forEach((r) => {
            if (r.barcode) map.set(r.barcode.toLocaleLowerCase('tr-TR'), r);
          });
        } catch (chunkErr) {
          console.warn('⚠️ Kategori eşleştirme parçası başarısız:', chunkErr);
        }
      }
      setFolderProductInfo(map);
      console.log('✅ Kategori eşleştirmesi tamamlandı:', map.size, 'barkod');
    } catch (error) {
      console.warn('⚠️ Klasör-ürün eşleştirme hatası:', error);
    } finally {
      setEnrichingCategories(false);
    }
  };

  // Klasör içindeki resimleri yükle
  const loadImagesInFolder = async (folderId: string) => {
    if (!companyId) return;

    setLoadingImages(true);
    setImages([]); // ✅ Önce state'i temizle - eski resimler gösterilmesin!
    
    try {
      // ✅ SQL API'sinden klasöre ait resimleri çek
      const { ImageLibraryImageService } = await import('./services/imageLibraryService');
      const imagesList = await ImageLibraryImageService.getImagesByFolder(companyId, folderId);

      const mappedImages: ImageItem[] = imagesList.map(image => ({
        id: image.id,
        name: image.name,
        url: image.url,
        thumbnailUrl: image.thumbnailUrl,
        folderId: image.folderId || '',
        createdAt: image.createdAt,
        createdBy: image.createdBy || '',
        size: image.size,
        type: image.type,
        mediaType: image.mediaType
      }));

      mappedImages.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      setImages(mappedImages);
      console.log('✅ Klasör medyaları SQL\'den yüklendi:', mappedImages.length, 'adet');
    } catch (error) {
      console.error('❌ Resimler yüklenirken hata:', error);
      setError(tt('Resimler yüklenirken bir hata oluştu.', 'An error occurred while loading images.'));
    } finally {
      setLoadingImages(false);
    }
  };

  // Ana dizindeki resimleri yükle (folderId olmayan resimler)
  const loadImagesInMainDirectory = async () => {
    if (!companyId) return;

    setLoadingImages(true);
    setImages([]); // ✅ Önce state'i temizle - eski resimler gösterilmesin!
    
    try {
      // ✅ SQL API'sinden ana dizin resimlerini çek (folderId = undefined)
      const { ImageLibraryImageService } = await import('./services/imageLibraryService');
      const imagesList = await ImageLibraryImageService.getImagesByFolder(companyId, undefined);

      console.log('📊 SQL\'den gelen resimler (ana dizin):', imagesList);

      const mappedImages: ImageItem[] = imagesList.map(image => ({
        id: image.id,
        name: image.name,
        url: image.url,
        thumbnailUrl: image.thumbnailUrl,
        folderId: image.folderId || '',
        createdAt: image.createdAt,
        createdBy: image.createdBy || '',
        size: image.size,
        type: image.type,
        mediaType: image.mediaType
      }));

      mappedImages.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      setImages(mappedImages);
      console.log('✅ Ana dizin medyaları SQL\'den yüklendi:', mappedImages.length, 'adet');
    } catch (error) {
      console.error('❌ Ana dizin resimleri yüklenirken hata:', error);
      setError(tt('Ana dizin resimleri yüklenirken bir hata oluştu.', 'An error occurred while loading main directory images.'));
    } finally {
      setLoadingImages(false);
    }
  };

  // Klasöre tıklama - klasör içine gir
  const handleFolderClick = async (folder: ImageFolder) => {
    setSelectedFolder(folder);
    // Klasör resimlerini yükle
    await loadImagesInFolder(folder.id);
  };

  // Ana klasör listesine dön
  const handleBackToFolders = async () => {
    setSelectedFolder(null);
    // Ana dizin resimlerini yükle
    await loadImagesInMainDirectory();
  };

  // Geri dönme işlevi
  const handleGoBack = () => {
    if (selectedFolder) {
      // Klasör içindeyken ana klasör listesine dön
      handleBackToFolders();
    } else {
      // Ana klasör listesindeyken stok sayfasına dön
      navigate('/stok');
    }
  };

  // Klasör oluşturma modalını aç
  const handleOpenFolderModal = () => {
    setFolderName('');
    setError(null);
    setFolderColor('#2196F3');
    setFolderBranchId('');
    setIsFolderModalOpen(true);
  };

  // Klasör oluşturma modalını kapat
  const handleCloseFolderModal = () => {
    if (isCreatingFolder) return; // İşlem devam ediyorsa kapatma
    setIsFolderModalOpen(false);
    setFolderName('');
    setFolderColor('#2196F3');
    setFolderBranchId('');
    setError(null);
  };

  // Klasör oluştur
  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      setError(tt('Klasör adı boş olamaz', 'Folder name cannot be empty'));
      return;
    }

    if (!companyId) {
      setError(tt('Şirket bilgisi bulunamadı', 'Company information not found'));
      return;
    }

    // Aynı isimde klasör var mı kontrol et
    const existingFolder = folders.find(folder => 
      folder.name.toLowerCase() === folderName.trim().toLowerCase()
    );
    
    if (existingFolder) {
      setError(tt('Bu isimde bir klasör zaten mevcut', 'A folder with this name already exists'));
      return;
    }

    setIsCreatingFolder(true);
    setError(null);

    try {
      if (!currentUser) {
        throw new Error(tt('Kullanıcı oturumu açık değil', 'User session is not active'));
      }

      // ✅ SQL API'sine klasör oluştur
      const { ImageLibraryFolderService } = await import('./services/imageLibraryService');
      
      const newFolder = await ImageLibraryFolderService.createFolder({
        companyId: companyId,
        branchId: folderBranchId || undefined,
        name: folderName.trim(),
        color: folderColor,
        createdBy: currentUser.uid,
      });

      console.log('✅ Klasör SQL\'e kaydedildi:', newFolder);

      // Klasörleri yeniden yükle
      await loadFolders(companyId);

      // Modalı kapat
      handleCloseFolderModal();
    } catch (error: any) {
      console.error('❌ Klasör oluşturulurken hata:', error);
      setError(error.response?.data?.message || tt('Klasör oluşturulurken bir hata oluştu', 'An error occurred while creating folder'));
    } finally {
      setIsCreatingFolder(false);
    }
  };

  // Enter tuşu ile klasör oluştur
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isCreatingFolder) {
      handleCreateFolder();
    }
  };

  // Resim yükleme modalını aç
  const handleOpenUploadModal = () => {
    setError(null);
    setIsUploadModalOpen(true);
  };

  // Resim yükleme modalını kapat
  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  // Video önizleme
  const handleVideoClick = (video: ImageItem) => {
    setPreviewVideoUrl(video.url);
    setPreviewVideoName(video.name);
    setIsVideoPreviewOpen(true);
  };

  const handleVideoPreviewClose = () => {
    setIsVideoPreviewOpen(false);
    setPreviewVideoUrl('');
    setPreviewVideoName('');
  };

  // Video yükleme modalını aç
  const handleOpenVideoUploadModal = () => {
    setError(null);
    setIsVideoUploadModalOpen(true);
  };

  // Video yükleme modalını kapat
  const handleCloseVideoUploadModal = () => {
    setIsVideoUploadModalOpen(false);
  };

  // Resim yükleme tamamlandığında
  const handleUploadComplete = async () => {
    // Klasör listesini ve resim listesini yeniden yükle
    if (companyId) {
      await loadFolders(companyId);
      
      if (selectedFolder) {
        await loadImagesInFolder(selectedFolder.id);
      } else {
        await loadImagesInMainDirectory();
      }
    }
  };

  // Resim önizleme modalını aç
  const handleImageClick = (clickedImage: ImageItem, allImages: ImageItem[]) => {
    const imageIndex = allImages.findIndex(img => img.id === clickedImage.id);
    setPreviewImages(allImages);
    setPreviewImageIndex(imageIndex >= 0 ? imageIndex : 0);
    setIsPreviewModalOpen(true);
  };

  // Preview modal'ı kapat
  const handlePreviewClose = () => {
    setIsPreviewModalOpen(false);
    setPreviewImages([]);
    setPreviewImageIndex(0);
  };

  // Preview modal'da resim değiştir
  const handlePreviewImageChange = (newIndex: number) => {
    setPreviewImageIndex(newIndex);
  };

  // Context menu handlers
  const handleFolderRightClick = (event: React.MouseEvent, folder: ImageFolder) => {
    event.preventDefault();
    event.stopPropagation();
    
    setContextMenu({
      open: true,
      anchorPosition: {
        top: event.clientY,
        left: event.clientX
      },
      folder
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu({
      open: false,
      anchorPosition: null,
      folder: null
    });
  };

  const handleContextMenuEdit = () => {
    if (contextMenu.folder) {
      setSelectedFolderForAction(contextMenu.folder);
      setIsFolderEditModalOpen(true);
    }
    handleContextMenuClose();
  };

  const handleContextMenuDelete = () => {
    if (contextMenu.folder) {
      setSelectedFolderForAction(contextMenu.folder);
      setIsFolderDeleteModalOpen(true);
    }
    handleContextMenuClose();
  };

  // Klasör güncelleme işlemi
  const handleFolderUpdate = async (folderId: string, newName: string, newColor: string) => {
    if (!companyId) return;

    try {
      console.log('🔄 Klasör güncelleme başladı:', { folderId, newName, newColor });
      
      // ✅ SQL API'sine klasör güncelleme isteği gönder (POST /api/ImageLibrary/folders/{companyId}/{id}/update)
      const { ImageLibraryFolderService } = await import('./services/imageLibraryService');
      await ImageLibraryFolderService.updateFolder(companyId, folderId, {
        name: newName,
        color: newColor
      });

      console.log('✅ Klasör güncellendi:', folderId);
      
      // Klasör listesini yenile
      await loadFolders(companyId);
      
      // Eğer güncellenen klasör şu anda açıksa, selectedFolder'ı da güncelle
      if (selectedFolder && selectedFolder.id === folderId) {
        const updatedFolder = { ...selectedFolder, name: newName, color: newColor };
        setSelectedFolder(updatedFolder);
      }
      
      console.log('✅ Klasör listesi yenilendi');
    } catch (error: any) {
      console.error('❌ Klasör güncelleme hatası:', error);
      throw new Error(error.response?.data?.message || tt('Klasör güncellenirken bir hata oluştu', 'An error occurred while updating folder'));
    }
  };

  // Klasör silme işlemi
  const handleFolderDelete = async (folderId: string) => {
    if (!companyId) return;

    try {
      // ✅ SQL API'sine klasör silme isteği gönder (Soft Delete)
      const { ImageLibraryFolderService } = await import('./services/imageLibraryService');
      await ImageLibraryFolderService.deleteFolder(companyId, folderId);

      console.log('✅ Klasör silindi (soft delete):', folderId);

      // Eğer silinen klasör şu anda seçili klasörse ana sayfaya dön
      if (selectedFolder && selectedFolder.id === folderId) {
        await handleBackToFolders();
      }

      // Klasör listesini yenile
      await loadFolders(companyId);
    } catch (error: any) {
      console.error('❌ Klasör silme hatası:', error);
      throw new Error(error.response?.data?.message || tt('Klasör silinirken bir hata oluştu', 'An error occurred while deleting folder'));
    }
  };

  // Resim sağ tık handlers
  const handleImageRightClick = (event: React.MouseEvent, image: ImageItem) => {
    event.preventDefault();
    event.stopPropagation();
    
    setImageContextMenu({
      open: true,
      anchorPosition: {
        top: event.clientY,
        left: event.clientX
      },
      image
    });
  };

  const handleImageContextMenuClose = () => {
    setImageContextMenu({
      open: false,
      anchorPosition: null,
      image: null
    });
  };

  const handleImageContextMenuPreview = () => {
    if (imageContextMenu.image) {
      // Mevcut klasördeki tüm resimleri al
      const currentImages = selectedFolder ? images : images;
      handleImageClick(imageContextMenu.image, currentImages);
    }
    handleImageContextMenuClose();
  };

  const handleImageContextMenuEdit = () => {
    if (imageContextMenu.image) {
      setSelectedImageForAction(imageContextMenu.image);
      setIsImageEditModalOpen(true);
    }
    handleImageContextMenuClose();
  };

  const handleImageContextMenuMove = () => {
    if (imageContextMenu.image) {
      setSelectedImageForAction(imageContextMenu.image);
      setIsImageMoveModalOpen(true);
    }
    handleImageContextMenuClose();
  };

  const handleImageContextMenuDownload = () => {
    if (imageContextMenu.image) {
      const link = document.createElement('a');
      link.href = imageContextMenu.image.url;
      link.download = imageContextMenu.image.name || `${tt('image', 'image')}-${Date.now()}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    handleImageContextMenuClose();
  };

  const handleImageContextMenuInfo = () => {
    if (imageContextMenu.image) {
      setSelectedImageForAction(imageContextMenu.image);
      setIsImageInfoModalOpen(true);
    }
    handleImageContextMenuClose();
  };

  const handleImageContextMenuDelete = () => {
    if (imageContextMenu.image) {
      setSelectedImageForAction(imageContextMenu.image);
      setIsImageDeleteModalOpen(true);
    }
    handleImageContextMenuClose();
  };

  // Resim güncelleme işlemi
  const handleImageUpdate = async (imageId: string, newName: string) => {
    if (!companyId) return;

    try {
      console.log('🔄 Resim adı güncelleme başladı:', { imageId, newName });
      
      // Mevcut resmi bul - folderId'yi korumak için gerekli
      const currentImage = images.find(img => img.id === imageId);
      if (!currentImage) {
        throw new Error(tt('Resim bulunamadı', 'Image not found'));
      }
      
      // ✅ SQL API'sine resim güncelleme isteği gönder (POST /api/ImageLibrary/images/{companyId}/{id}/update)
      const { ImageLibraryImageService } = await import('./services/imageLibraryService');
      await ImageLibraryImageService.updateImage(companyId, imageId, {
        name: newName,
        folderId: currentImage.folderId || undefined // Klasörü değiştirmiyoruz, sadece adı güncelliyoruz
      });

      console.log('✅ Resim adı güncellendi:', imageId);
      
      // Resim listesini yenile
      if (selectedFolder) {
        await loadImagesInFolder(selectedFolder.id);
      } else {
        await loadImagesInMainDirectory();
      }
      
      console.log('✅ Resim listesi yenilendi');
    } catch (error: any) {
      console.error('❌ Resim güncelleme hatası:', error);
      throw new Error(error.response?.data?.message || tt('Resim güncellenirken bir hata oluştu', 'An error occurred while updating image'));
    }
  };

  // Resim taşıma işlemi
  const handleImageMove = async (imageId: string, newFolderId: string) => {
    if (!companyId) return;

    try {
      console.log('🔄 Resim taşıma başladı:', { imageId, newFolderId });
      
      // Mevcut resmi bul - name'i korumak için gerekli
      const currentImage = images.find(img => img.id === imageId);
      if (!currentImage) {
        throw new Error(tt('Resim bulunamadı', 'Image not found'));
      }
      
      // ✅ SQL API'sine resim güncelleme isteği gönder (POST /api/ImageLibrary/images/{companyId}/{id}/update)
      const { ImageLibraryImageService } = await import('./services/imageLibraryService');
      await ImageLibraryImageService.updateImage(companyId, imageId, {
        name: currentImage.name, // Adı değiştirmiyoruz, sadece klasörü güncelliyoruz
        folderId: newFolderId || undefined // Boş string ise undefined gönder (ana dizin)
      });

      console.log('✅ Resim taşındı:', imageId);
      
      // Resim listesini yenile
      if (selectedFolder) {
        await loadImagesInFolder(selectedFolder.id);
      } else {
        await loadImagesInMainDirectory();
      }
      
      // Klasör listesini de yenile (imageCount güncellenmiş olabilir)
      await loadFolders(companyId);
      
      console.log('✅ Resim ve klasör listeleri yenilendi');
    } catch (error: any) {
      console.error('❌ Resim taşıma hatası:', error);
      throw new Error(error.response?.data?.message || tt('Resim taşınırken bir hata oluştu', 'An error occurred while moving image'));
    }
  };

  // Resim silme işlemi
  const handleImageDelete = async (imageId: string) => {
    if (!companyId) return;

    try {
      console.log('🗑️ Resim silme başladı:', imageId);
      
      // ✅ SQL API'sine resim silme isteği gönder (Soft Delete)
      const { ImageLibraryImageService } = await import('./services/imageLibraryService');
      await ImageLibraryImageService.deleteImage(companyId, imageId);

      console.log('✅ Resim silindi (soft delete):', imageId);
      console.log('🔄 Resim listesi yenileniyor...');

      // Resim listesini yenile
      if (selectedFolder) {
        await loadImagesInFolder(selectedFolder.id);
      } else {
        await loadImagesInMainDirectory();
      }

      console.log('✅ Resim listesi yenilendi');

      // Klasör listesini de yenile (ImageCount güncellenmiş olabilir)
      await loadFolders(companyId);
      
      console.log('✅ Klasör listesi de yenilendi');
    } catch (error: any) {
      console.error('❌ Resim silme hatası:', error);
      throw new Error(error.response?.data?.message || tt('Resim silinirken bir hata oluştu', 'An error occurred while deleting image'));
    }
  };

  // Klasör resim sayılarını güncelle
  const updateFolderImageCounts = async () => {
    // ✅ SQL'de ImageCount otomatik güncelleniyor, bu fonksiyon artık gerekli değil
    console.log('ℹ️ SQL\'de ImageCount otomatik güncelleniyor');
  };

  // Dosya boyutunu formatla
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Görünüm boyutuna göre grid item boyutları
  const getGridSizes = () => {
    if (isMobile) {
      // Mobil için basitleştirilmiş grid
      return { xs: 6, sm: 6, md: 4, lg: 3 };
    }
    
    // Desktop için viewSize'a göre
    switch (viewSize) {
      case 'small':
        return { xs: 6, sm: 4, md: 3, lg: 2 };
      case 'medium':
        return { xs: 12, sm: 6, md: 4, lg: 3 };
      case 'large':
        return { xs: 12, sm: 6, md: 4, lg: 3 };
      case 'xlarge':
        return { xs: 12, sm: 12, md: 6, lg: 4 };
      default:
        return { xs: 12, sm: 6, md: 4, lg: 3 };
    }
  };

  // Görünüm boyutuna göre resim ve ikon boyutları
  const getImageHeight = () => {
    if (isMobile) {
      return 150; // Mobilde sabit boyut
    }
    
    switch (viewSize) {
      case 'small': return 120;
      case 'medium': return 200;
      case 'large': return 280;
      case 'xlarge': return 360;
      default: return 200;
    }
  };

  const getIconSize = () => {
    if (isMobile) {
      return 40; // Mobilde sabit boyut
    }
    
    switch (viewSize) {
      case 'small': return 32;
      case 'medium': return 48;
      case 'large': return 64;
      case 'xlarge': return 80;
      default: return 48;
    }
  };

  // Filtre uygula
  // Klasör adına (barkod) karşılık gelen ürün eşleşmesini getir
  const getFolderProduct = (folderName: string): BarcodeResolveResult | undefined =>
    folderProductInfo.get((folderName || '').toLocaleLowerCase('tr-TR'));

  // Arama sorgusu bir klasörle eşleşiyor mu? (isim/barkod + ürün adı/stok kodu/kategori)
  const folderMatchesSearch = (folder: ImageFolder, q: string): boolean => {
    if (!q) return true;
    const needle = q.toLocaleLowerCase('tr-TR');
    if ((folder.name || '').toLocaleLowerCase('tr-TR').includes(needle)) return true;
    const p = getFolderProduct(folder.name);
    if (p?.found) {
      if ((p.name || '').toLocaleLowerCase('tr-TR').includes(needle)) return true;
      if ((p.stockCode || '').toLocaleLowerCase('tr-TR').includes(needle)) return true;
      if ((p.categoryName || '').toLocaleLowerCase('tr-TR').includes(needle)) return true;
    }
    return false;
  };

  const imageMatchesSearch = (image: ImageItem, q: string): boolean => {
    if (!q) return true;
    return (image.name || '').toLocaleLowerCase('tr-TR').includes(q.toLocaleLowerCase('tr-TR'));
  };

  // Kategori filtresi klasöre uygulanır (barkod -> ürün -> kategori)
  const folderMatchesCategory = (folder: ImageFolder): boolean => {
    if (categoryFilter === 'all') return true;
    const p = getFolderProduct(folder.name);
    if (categoryFilter === 'none') return !p?.found || !p.categoryName;
    return !!p?.found && p.categoryName === categoryFilter;
  };

  const getFilteredItems = () => {
    const q = searchQuery.trim();
    if (selectedFolder) {
      // Klasör içindeyken sadece resimler (arama uygulanır)
      return images.filter((img) => imageMatchesSearch(img, q));
    } else {
      // Şube + kategori + arama filtreleri klasörlere uygulanır
      const branchFilteredFolders = folders.filter(folder => {
        const branchOk =
          branchFilter === 'all'
            ? true
            : branchFilter === 'none'
            ? !folder.branchId
            : String(folder.branchId) === String(branchFilter);
        return branchOk && folderMatchesCategory(folder) && folderMatchesSearch(folder, q);
      });

      // Kategori filtresi aktifken serbest resimleri gizle (kategori sadece klasörlerde var)
      const filteredImages =
        categoryFilter !== 'all' ? [] : images.filter((img) => imageMatchesSearch(img, q));

      // Ana dizinde hem klasörler hem resimler
      switch (filterType) {
        case 'folders':
          return branchFilteredFolders;
        case 'images':
          return filteredImages;
        case 'all':
        default:
          return [...branchFilteredFolders, ...filteredImages];
      }
    }
  };

  // Kategori filtresi için mevcut kategoriler (çözülen ürünlerden)
  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    folderProductInfo.forEach((p) => {
      if (p.found && p.categoryName) set.add(p.categoryName);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [folderProductInfo]);

  // Resim listeleme için thumbnail kullan
  const getImageUrl = (image: ImageItem) => {
    // Listede thumbnail göster, tıklayınca orijinal gösterilecek
    return image.thumbnailUrl || image.url;
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: { xs: 2, md: 3 },
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      onContextMenu={(e) => {
        // Sadece container'ın kendisinde sağ tık olursa varsayılan menüyü engelle
        if (e.target === e.currentTarget) {
          e.preventDefault();
        }
      }}
    >
      {/* Üst Başlık ve Butonlar */}
      <Box sx={{ mb: 2 }}>
        {/* Header - Responsive */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          mb: 2
        }}>
          {/* Sol Taraf - Geri Butonu ve Başlık */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' } }}>
            {isMobile ? (
              <IconButton
                onClick={handleGoBack}
                color="primary"
                sx={{ 
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                color="primary"
              >
                {tt('Geri', 'Back')}
              </Button>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" fontWeight="bold">
                  {tt('Resim Kütüphanesi', 'Image Library')}
                </Typography>
                <GuideButton guideKey="inventory.imageLibrary" size="small" />
              </Box>
              {/* Breadcrumb navigasyon */}
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleBackToFolders}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    textDecoration: selectedFolder ? 'underline' : 'none',
                    color: selectedFolder ? 'primary.main' : 'text.primary',
                    cursor: selectedFolder ? 'pointer' : 'default'
                  }}
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {tt('Ana Sayfa', 'Home')}
                </Link>
                {selectedFolder && (
                  <Typography color="text.primary" variant="body2">
                    {selectedFolder.name}
                  </Typography>
                )}
              </Breadcrumbs>
            </Box>
          </Box>
          
          {/* Sağ Taraf - Butonlar */}
          {isMobile ? (
            // Mobil: 2x2 Grid
            <Grid container spacing={1} sx={{ width: '100%' }}>
              {!selectedFolder && (
                <Grid item xs={6}>
                  <Button 
                    fullWidth
                    variant="outlined" 
                    startIcon={<CreateNewFolderIcon />}
                    onClick={handleOpenFolderModal}
                    color="primary"
                    size="small"
                  >
                    {tt('Klasör', 'Folder')}
                  </Button>
                </Grid>
              )}
              <Grid item xs={!selectedFolder ? 6 : 12}>
                <Button 
                  fullWidth
                  variant="outlined" 
                  startIcon={<CloudUploadIcon />}
                  onClick={handleOpenUploadModal}
                  color="primary"
                  size="small"
                >
                  {tt('Upload', 'Upload')}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button 
                  fullWidth
                  variant="outlined" 
                  startIcon={<VideoLibraryIcon />}
                  onClick={handleOpenVideoUploadModal}
                  color="secondary"
                  size="small"
                >
                  {tt('Video', 'Video')}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DriveFolderUploadIcon />}
                  onClick={() => setIsBulkAssignOpen(true)}
                  color="primary"
                  size="small"
                >
                  {tt('Toplu Ürün Resmi', 'Bulk Product Images')}
                </Button>
              </Grid>
            </Grid>
          ) : (
            // Desktop: Normal ButtonGroup
            <Grid container spacing={2} justifyContent="flex-end" sx={{ width: 'auto' }}>
              <Grid item>
                <ButtonGroup>
                  {/* Klasör oluştur butonu sadece ana sayfada göster */}
                  {!selectedFolder && (
                    <Button 
                      variant="outlined" 
                      startIcon={<CreateNewFolderIcon />}
                      onClick={handleOpenFolderModal}
                      color="primary"
                    >
                      {tt('Klasör Oluştur', 'Create Folder')}
                    </Button>
                  )}
                  <Button 
                    variant="outlined" 
                    startIcon={<CloudUploadIcon />}
                    onClick={handleOpenUploadModal}
                    color="primary"
                  >
                    {tt('Resim Yükle', 'Upload Image')}
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    startIcon={<VideoLibraryIcon />}
                    onClick={handleOpenVideoUploadModal}
                    color="secondary"
                  >
                    {tt('Video Yükle', 'Upload Video')}
                  </Button>
                </ButtonGroup>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<DriveFolderUploadIcon />}
                  onClick={() => setIsBulkAssignOpen(true)}
                  color="primary"
                >
                  {tt('Toplu Ürün Resmi Yükle', 'Bulk Product Image Upload')}
                </Button>
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Görünüm ve Filtre Kontrolleri - Responsive */}
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, md: 2 }, 
          mb: 2, 
          alignItems: 'center',
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          {/* Akıllı Arama - isim / barkod / stok kodu / kategori */}
          <TextField
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={tt('İsim, barkod, kategori ara...', 'Search name, barcode, category...')}
            sx={{ minWidth: { xs: '100%', sm: 260 }, flexGrow: { sm: 1 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'action.active' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <InputLabel>{tt('Görünüm Boyutu', 'View Size')}</InputLabel>
            <Select
              value={viewSize}
              label={tt('Görünüm Boyutu', 'View Size')}
            onChange={(e) => handleViewSizeChange(e.target.value as 'small' | 'medium' | 'large' | 'xlarge')}
            startAdornment={<ViewModuleIcon sx={{ mr: 1, color: 'action.active' }} />}
          >
            <MenuItem value="small">{tt('Küçük Simgeler', 'Small Icons')}</MenuItem>
            <MenuItem value="medium">{tt('Orta Boyutlu Simgeler', 'Medium Icons')}</MenuItem>
            <MenuItem value="large">{tt('Büyük Simgeler', 'Large Icons')}</MenuItem>
            <MenuItem value="xlarge">{tt('Çok Büyük Simgeler', 'Extra Large Icons')}</MenuItem>
          </Select>
        </FormControl>

        {!selectedFolder && (
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>{tt('Filtre', 'Filter')}</InputLabel>
            <Select
              value={filterType}
              label={tt('Filtre', 'Filter')}
              onChange={(e) => handleFilterTypeChange(e.target.value as 'all' | 'folders' | 'images')}
              startAdornment={<FilterListIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              <MenuItem value="all">{tt('Tümü', 'All')}</MenuItem>
              <MenuItem value="folders">{tt('Sadece Klasörler', 'Folders Only')}</MenuItem>
              <MenuItem value="images">{tt('Sadece Resimler', 'Media Only')}</MenuItem>
            </Select>
          </FormControl>
        )}

        {!selectedFolder && filterType !== 'images' && (
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <InputLabel>{tt('Kategori', 'Category')}</InputLabel>
            <Select
              value={categoryFilter}
              label={tt('Kategori', 'Category')}
              onChange={(e) => setCategoryFilter(e.target.value)}
              startAdornment={<CategoryIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              <MenuItem value="all">
                {enrichingCategories
                  ? tt('Kategoriler yükleniyor...', 'Loading categories...')
                  : tt('Tüm Kategoriler', 'All Categories')}
              </MenuItem>
              <MenuItem value="none">{tt('Eşleşmeyen / Kategorisiz', 'Unmatched / No Category')}</MenuItem>
              {availableCategories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!selectedFolder && filterType !== 'images' && branches.filter(b => String(b.id) !== 'null').length > 0 && (
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel>{tt('Şube', 'Branch')}</InputLabel>
            <Select
              value={branchFilter}
              label={tt('Şube', 'Branch')}
              onChange={(e) => handleBranchFilterChange(e.target.value)}
              startAdornment={<FilterListIcon sx={{ mr: 1, color: 'action.active' }} />}
            >
              <MenuItem value="all">{tt('Tüm Şubeler', 'All Branches')}</MenuItem>
              <MenuItem value="none">{tt('Şubesiz', 'No Branch')}</MenuItem>
              {branches
                .filter((b) => String(b.id) !== 'null')
                .map((b) => (
                  <MenuItem key={String(b.id)} value={String(b.id)}>
                    {b.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
      </Box>
      </Box>

      {/* Ana İçerik - Kaydırmalı Alan */}
      <Box sx={{ 
        flex: 1,
        overflowY: 'auto',
        px: { xs: 0, md: 3 },
        py: { xs: 1, md: 3 },
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px' 
          }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', p: { xs: 2, md: 4 } }}>
            {/* Ana klasör listesi */}
            {!selectedFolder ? (
              folders.length === 0 && images.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '400px',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
                  borderRadius: 2
                }}>
                  <PhotoLibraryIcon 
                    sx={{ 
                      fontSize: 80, 
                      color: 'primary.main',
                      mb: 2
                    }} 
                  />
                  
                  <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
                    {tt('Resim Kütüphanesi', 'Image Library')}
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 600 }}>
                    {tt('Henüz klasör veya resim oluşturulmamış. Başlamak için yukarıdaki butonları kullanabilirsiniz.', 'No folder or image has been created yet. You can use the buttons above to get started.')}
                  </Typography>

                  <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1, maxWidth: 500 }}>
                    <Typography variant="subtitle2" color="info.contrastText" gutterBottom>
                      {tt('📁 Klasörlerle Düzenleyin:', '📁 Organize with Folders:')}
                    </Typography>
                    <Typography variant="body2" color="info.contrastText">
                      {tt('• Resimlerinizi kategorilere ayırın', '• Categorize your images')}<br/>
                      {tt('• Ana dizine doğrudan resim yükleyin', '• Upload images directly to the main directory')}<br/>
                      {tt('• Kolay arama ve bulma', '• Easy search and discovery')}<br/>
                      {tt('• Düzenli kütüphane yönetimi', '• Organized library management')}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box>
                  {(() => {
                    const filteredItems = getFilteredItems();
                    const folderItems = filteredItems.filter(item => 'imageCount' in item) as ImageFolder[];
                    const allMediaItems = filteredItems.filter(item => 'url' in item) as ImageItem[];
                    const onlyImages = allMediaItems.filter(item => item.mediaType !== 'video');
                    const onlyVideos = allMediaItems.filter(item => item.mediaType === 'video');
                    const totalItems = folderItems.length + allMediaItems.length;
                    
                    return (
                      <>
                        <Typography variant="h6" gutterBottom>
                          {filterType === 'all' ? `${tt('Tüm Öğeler', 'All Items')} (${totalItems})` :
                           filterType === 'folders' ? `${tt('Klasörler', 'Folders')} (${folderItems.length})` :
                           `${tt('Medya', 'Media')} (${allMediaItems.length})`}
                        </Typography>
                        
                        {/* Klasörler */}
                        {(filterType === 'all' || filterType === 'folders') && folderItems.length > 0 && (
                          <Grid container spacing={2} sx={{ mb: 3 }}>
                            {folderItems.map((folder) => (
                              <Grid item {...getGridSizes()} key={`folder-${folder.id}`}>
                                <Paper 
                                  elevation={1} 
                                  sx={{ 
                                    p: 2, 
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    height: 'fit-content',
                                    '&:hover': {
                                      elevation: 3,
                                      transform: 'translateY(-2px)',
                                      transition: 'all 0.3s ease'
                                    }
                                  }}
                                  onClick={() => handleFolderClick(folder)}
                                  onContextMenu={(e) => handleFolderRightClick(e, folder)}
                                >
                                  <FolderIcon 
                                    sx={{ 
                                      fontSize: getIconSize(), 
                                      color: folder.color || 'primary.main',
                                      mb: 1
                                    }} 
                                  />
                                  <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                    {folder.name}
                                  </Typography>
                                  {getBranchName(folder.branchId) && (
                                    <Chip
                                      label={getBranchName(folder.branchId)}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ my: 0.5, maxWidth: '100%' }}
                                    />
                                  )}
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {folder.imageCount || 0} {tt('medya', 'media')}
                                  </Typography>
                                  <br />
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(folder.createdAt).toLocaleDateString(localeCode)}
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        )}

                        {/* Resimler */}
                        {(filterType === 'all' || filterType === 'images') && onlyImages.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhotoLibraryIcon fontSize="small" color="primary" />
                              {tt('Resimler', 'Images')} ({onlyImages.length})
                            </Typography>
                            <Grid container spacing={2}>
                              {onlyImages.map((image) => (
                                <Grid item {...getGridSizes()} key={`image-${image.id}`}>
                                  <Paper 
                                    elevation={1} 
                                    sx={{ 
                                      overflow: 'hidden',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        elevation: 3,
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.3s ease'
                                      }
                                    }}
                                    onClick={() => handleImageClick(image, onlyImages)}
                                    onContextMenu={(e) => handleImageRightClick(e, image)}
                                  >
                                    <Box
                                      component="img"
                                      src={getImageUrl(image)}
                                      alt={image.name}
                                      sx={{
                                        width: '100%',
                                        height: getImageHeight(),
                                        objectFit: 'cover'
                                      }}
                                    />
                                    <Box sx={{ p: 2 }}>
                                      <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                        {image.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {image.size ? formatFileSize(image.size) : tt('Bilinmiyor', 'Unknown')}
                                      </Typography>
                                      <br />
                                      <Typography variant="caption" color="text.secondary">
                                        {new Date(image.createdAt).toLocaleDateString(localeCode)}
                                      </Typography>
                                    </Box>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}

                        {/* Videolar */}
                        {(filterType === 'all' || filterType === 'images') && onlyVideos.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <VideoLibraryIcon fontSize="small" color="secondary" />
                              {tt('Videolar', 'Videos')} ({onlyVideos.length})
                            </Typography>
                            <Grid container spacing={2}>
                              {onlyVideos.map((video) => (
                                <Grid item {...getGridSizes()} key={`video-${video.id}`}>
                                  <Paper 
                                    elevation={1} 
                                    sx={{ 
                                      overflow: 'hidden',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        elevation: 3,
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.3s ease',
                                        '& .video-play-overlay': { opacity: 1 }
                                      }
                                    }}
                                    onClick={() => handleVideoClick(video)}
                                    onContextMenu={(e) => handleImageRightClick(e, video)}
                                  >
                                    <Box sx={{
                                      width: '100%',
                                      height: getImageHeight(),
                                      position: 'relative',
                                      bgcolor: '#000'
                                    }}>
                                      <Box
                                        component="video"
                                        src={video.url}
                                        muted
                                        preload="metadata"
                                        sx={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                      />
                                      <Box className="video-play-overlay" sx={{
                                        position: 'absolute',
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(0,0,0,0.3)',
                                        opacity: 0.7,
                                        transition: 'opacity 0.2s'
                                      }}>
                                        <PlayCircleOutlineIcon sx={{ fontSize: 56, color: 'white' }} />
                                      </Box>
                                      <Chip
                                        label="MP4"
                                        size="small"
                                        color="secondary"
                                        sx={{ position: 'absolute', top: 8, right: 8 }}
                                      />
                                    </Box>
                                    <Box sx={{ p: 2 }}>
                                      <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                        {video.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {video.size ? formatFileSize(video.size) : tt('Bilinmiyor', 'Unknown')}
                                      </Typography>
                                      <br />
                                      <Typography variant="caption" color="text.secondary">
                                        {new Date(video.createdAt).toLocaleDateString(localeCode)}
                                      </Typography>
                                    </Box>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Box>
              )
            ) : (
              /* Klasör içi - resim listesi */
              <Box>
                {(() => {
                  const folderImages = images.filter(img => img.mediaType !== 'video');
                  const folderVideos = images.filter(img => img.mediaType === 'video');
                  return (
                    <Typography variant="h6" gutterBottom>
                      {selectedFolder.name} ({folderImages.length} {tt('resim', 'images')}, {folderVideos.length} {tt('video', 'videos')})
                    </Typography>
                  );
                })()}
                
                {loadingImages ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '300px' 
                  }}>
                    <CircularProgress />
                  </Box>
                ) : images.length === 0 ? (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '300px',
                      background: isDragOver 
                        ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' 
                        : 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
                      borderRadius: 2,
                      border: isDragOver ? '2px dashed #2196f3' : '2px dashed transparent',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                        transform: 'scale(1.02)',
                      }
                    }}
                    onClick={handleEmptyAreaClick}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    {isDragOver && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(33, 150, 243, 0.1)',
                          borderRadius: 2,
                          zIndex: 1
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <CloudUploadIcon 
                            sx={{ 
                              fontSize: 80, 
                              color: 'primary.main',
                              mb: 1,
                              animation: 'bounce 1s infinite'
                            }} 
                          />
                          <Typography variant="h5" color="primary.main" fontWeight="bold">
                            {tt('Resimleri Buraya Bırakın!', 'Drop Images Here!')}
                          </Typography>
                          <Typography variant="body2" color="primary.main">
                            {tt('Resim dosyalarınızı sürükleyip bırakabilirsiniz', 'You can drag and drop your image files')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    <CloudUploadIcon 
                      sx={{ 
                        fontSize: 64, 
                        color: isDragOver ? 'primary.main' : 'primary.main',
                        mb: 2,
                        opacity: isDragOver ? 0.3 : 1
                      }} 
                    />
                    
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom 
                      color="primary.main" 
                      fontWeight="bold"
                      sx={{ opacity: isDragOver ? 0.3 : 1 }}
                    >
                      {tt('Bu klasör boş', 'This folder is empty')}
                    </Typography>
                    
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      textAlign="center"
                      sx={{ opacity: isDragOver ? 0.3 : 1, mb: 2 }}
                    >
                      {tt('Resim yüklemek için tıklayın veya dosyaları sürükleyip bırakın', 'Click to upload images or drag and drop files')}
                    </Typography>

                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      flexWrap: 'wrap', 
                      justifyContent: 'center',
                      opacity: isDragOver ? 0.3 : 1
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: 'grey.100',
                        px: 2,
                        py: 1,
                        borderRadius: 1
                      }}>
                        {tt('🖱️ Tıklayarak yükle', '🖱️ Click to upload')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: 'grey.100',
                        px: 2,
                        py: 1,
                        borderRadius: 1
                      }}>
                        {tt('🖼️ Sürükle & Bırak', '🖼️ Drag & Drop')}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Klasör içi - Resimler */}
                    {(() => {
                      const folderImages = images.filter(img => img.mediaType !== 'video');
                      const folderVideos = images.filter(img => img.mediaType === 'video');
                      return (
                        <>
                          {folderImages.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhotoLibraryIcon fontSize="small" color="primary" />
                                {tt('Resimler', 'Images')} ({folderImages.length})
                              </Typography>
                              <Grid container spacing={2}>
                                {folderImages.map((image) => (
                                  <Grid item {...getGridSizes()} key={image.id}>
                                    <Paper 
                                      elevation={1} 
                                      sx={{ 
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          elevation: 3,
                                          transform: 'translateY(-2px)',
                                          transition: 'all 0.3s ease'
                                        }
                                      }}
                                      onClick={() => handleImageClick(image, folderImages)}
                                      onContextMenu={(e) => handleImageRightClick(e, image)}
                                    >
                                      <Box
                                        component="img"
                                        src={getImageUrl(image)}
                                        alt={image.name}
                                        sx={{
                                          width: '100%',
                                          height: getImageHeight(),
                                          objectFit: 'cover'
                                        }}
                                      />
                                      <Box sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                          {image.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {image.size ? formatFileSize(image.size) : tt('Bilinmiyor', 'Unknown')}
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="text.secondary">
                                          {new Date(image.createdAt).toLocaleDateString(localeCode)}
                                        </Typography>
                                      </Box>
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}

                          {/* Klasör içi - Videolar */}
                          {folderVideos.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <VideoLibraryIcon fontSize="small" color="secondary" />
                                {tt('Videolar', 'Videos')} ({folderVideos.length})
                              </Typography>
                              <Grid container spacing={2}>
                                {folderVideos.map((video) => (
                                  <Grid item {...getGridSizes()} key={video.id}>
                                    <Paper 
                                      elevation={1} 
                                      sx={{ 
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          elevation: 3,
                                          transform: 'translateY(-2px)',
                                          transition: 'all 0.3s ease',
                                          '& .video-play-overlay': { opacity: 1 }
                                        }
                                      }}
                                      onClick={() => handleVideoClick(video)}
                                      onContextMenu={(e) => handleImageRightClick(e, video)}
                                    >
                                      <Box sx={{
                                        width: '100%',
                                        height: getImageHeight(),
                                        position: 'relative',
                                        bgcolor: '#000'
                                      }}>
                                        <Box
                                          component="video"
                                          src={video.url}
                                          muted
                                          preload="metadata"
                                          sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                          }}
                                        />
                                        <Box className="video-play-overlay" sx={{
                                          position: 'absolute',
                                          top: 0, left: 0, right: 0, bottom: 0,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          bgcolor: 'rgba(0,0,0,0.3)',
                                          opacity: 0.7,
                                          transition: 'opacity 0.2s'
                                        }}>
                                          <PlayCircleOutlineIcon sx={{ fontSize: 56, color: 'white' }} />
                                        </Box>
                                        <Chip
                                          label="MP4"
                                          size="small"
                                          color="secondary"
                                          sx={{ position: 'absolute', top: 8, right: 8 }}
                                        />
                                      </Box>
                                      <Box sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                          {video.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {video.size ? formatFileSize(video.size) : tt('Bilinmiyor', 'Unknown')}
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="text.secondary">
                                          {new Date(video.createdAt).toLocaleDateString(localeCode)}
                                        </Typography>
                                      </Box>
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </Box>
            )}
          </Paper>
        )}
        
        {/* Sayfa altında ek boşluk */}
        <Box sx={{ height: '100px' }} />
      </Box>

      {/* Klasör Oluşturma Modalı */}
      <Dialog
        open={isFolderModalOpen}
        onClose={!isCreatingFolder ? handleCloseFolderModal : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{tt('Yeni Klasör Oluştur', 'Create New Folder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={tt('Klasör Adı', 'Folder Name')}
            type="text"
            fullWidth
            variant="outlined"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isCreatingFolder}
            placeholder={tt('Klasör adını giriniz...', 'Enter folder name...')}
            helperText={tt('Örnek: Ürün Resimleri, Logo Koleksiyonu, Banner Resimleri', 'Example: Product Images, Logo Collection, Banner Images')}
          />
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            {tt('Klasör Rengi', 'Folder Color')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {folderColors.map((color) => (
              <Box
                key={color}
                onClick={() => setFolderColor(color)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: color,
                  cursor: 'pointer',
                  border: folderColor === color ? '3px solid #333' : `3px solid transparent`,
                  boxShadow: folderColor === color ? '0 0 0 2px #fff, 0 0 0 4px #333' : 'rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 4px 8px 0px, rgba(0, 0, 0, 0.1) 0px 2px 4px 0px'
                  }
                }}
              />
            ))}
          </Box>
          <FormControl fullWidth size="small" sx={{ mt: 3 }}>
            <InputLabel id="folder-branch-label">{tt('Şube (opsiyonel)', 'Branch (optional)')}</InputLabel>
            <Select
              labelId="folder-branch-label"
              value={folderBranchId}
              label={tt('Şube (opsiyonel)', 'Branch (optional)')}
              onChange={(e) => setFolderBranchId(e.target.value)}
              disabled={isCreatingFolder}
            >
              <MenuItem value="">
                <em>{tt('Şube seçilmedi (tüm şirket)', 'No branch (whole company)')}</em>
              </MenuItem>
              {branches
                .filter((b) => String(b.id) !== 'null')
                .map((b) => (
                  <MenuItem key={String(b.id)} value={String(b.id)}>
                    {b.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseFolderModal} 
            color="inherit"
            disabled={isCreatingFolder}
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleCreateFolder} 
            variant="contained"
            color="primary" 
            disabled={isCreatingFolder || !folderName.trim()}
            startIcon={isCreatingFolder ? <CircularProgress size={20} /> : <CreateNewFolderIcon />}
          >
            {isCreatingFolder ? tt('Oluşturuluyor...', 'Creating...') : tt('Klasör Oluştur', 'Create Folder')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resim Yükleme Modalı */}
      <ImageUploadModal
        open={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        companyId={companyId}
        selectedFolder={selectedFolder}
        onUploadComplete={handleUploadComplete}
      />

      {/* Resim Önizleme Modalı */}
      <ImagePreviewModal
        open={isPreviewModalOpen}
        onClose={handlePreviewClose}
        images={previewImages}
        currentImageIndex={previewImageIndex}
        onImageChange={handlePreviewImageChange}
      />

      {/* Context Menu */}
      <ContextMenu
        open={contextMenu.open}
        onClose={handleContextMenuClose}
        anchorPosition={contextMenu.anchorPosition}
        onEdit={handleContextMenuEdit}
        onDelete={handleContextMenuDelete}
        itemName={contextMenu.folder?.name || ''}
      />

      {/* Klasör Düzenleme Modalı */}
      <FolderEditModal
        open={isFolderEditModalOpen}
        onClose={() => setIsFolderEditModalOpen(false)}
        folder={selectedFolderForAction}
        onUpdate={handleFolderUpdate}
      />

      {/* Klasör Silme Modalı */}
      <FolderDeleteModal
        open={isFolderDeleteModalOpen}
        onClose={() => setIsFolderDeleteModalOpen(false)}
        folder={selectedFolderForAction}
        onDelete={handleFolderDelete}
      />

      {/* Resim Context Menu */}
      <ImageContextMenu
        open={imageContextMenu.open}
        onClose={handleImageContextMenuClose}
        anchorPosition={imageContextMenu.anchorPosition}
        onPreview={handleImageContextMenuPreview}
        onEdit={handleImageContextMenuEdit}
        onMove={handleImageContextMenuMove}
        onDownload={handleImageContextMenuDownload}
        onInfo={handleImageContextMenuInfo}
        onDelete={handleImageContextMenuDelete}
        imageName={imageContextMenu.image?.name || ''}
      />

      {/* Resim Düzenleme Modalı */}
      <ImageEditModal
        open={isImageEditModalOpen}
        onClose={() => setIsImageEditModalOpen(false)}
        image={selectedImageForAction}
        onSave={handleImageUpdate}
        existingImages={images}
      />

      {/* Resim Taşıma Modalı */}
      <ImageMoveModal
        open={isImageMoveModalOpen}
        onClose={() => setIsImageMoveModalOpen(false)}
        image={selectedImageForAction}
        onMove={handleImageMove}
        availableFolders={folders}
        currentFolderId={selectedFolder?.id || ''}
      />

      {/* Resim Silme Modalı */}
      <ImageDeleteModal
        open={isImageDeleteModalOpen}
        onClose={() => setIsImageDeleteModalOpen(false)}
        image={selectedImageForAction}
        onDelete={handleImageDelete}
      />

      {/* Resim Bilgileri Modalı */}
      <ImageInfoModal
        open={isImageInfoModalOpen}
        onClose={() => setIsImageInfoModalOpen(false)}
        image={selectedImageForAction}
        folders={folders}
      />

      {/* Video Yükleme Modalı */}
      <VideoUploadModal
        open={isVideoUploadModalOpen}
        onClose={handleCloseVideoUploadModal}
        companyId={companyId}
        selectedFolder={selectedFolder}
        onUploadComplete={handleUploadComplete}
      />

      {/* Toplu Ürün Resmi Yükleme Sihirbazı */}
      <BulkProductImageAssignWizard
        open={isBulkAssignOpen}
        onClose={() => setIsBulkAssignOpen(false)}
        companyId={companyId}
        createdBy={currentUser?.uid}
        onCompleted={() => {
          if (!companyId) return;
          loadFolders(companyId);
          if (selectedFolder) loadImagesInFolder(selectedFolder.id);
          else loadImagesInMainDirectory();
        }}
      />

      {/* Video Önizleme Modalı */}
      <Dialog
        open={isVideoPreviewOpen}
        onClose={handleVideoPreviewClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#000',
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1,
          px: 2,
          bgcolor: 'rgba(0,0,0,0.8)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VideoLibraryIcon />
            <Typography variant="subtitle1" noWrap>{previewVideoName}</Typography>
          </Box>
          <IconButton onClick={handleVideoPreviewClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', bgcolor: '#000' }}>
          {previewVideoUrl && (
            <Box
              component="video"
              src={previewVideoUrl}
              controls
              autoPlay
              sx={{
                width: '100%',
                maxHeight: '75vh',
                outline: 'none'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ImageLibrary; 